import { initializeApp } from 'firebase/app';
import { 
  getAuth, 
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signInWithPopup,
  GoogleAuthProvider,
  signOut,
  sendPasswordResetEmail,
  sendEmailVerification,
  onAuthStateChanged,
  updateProfile
} from 'firebase/auth';
import { 
  getFirestore, 
  doc, 
  setDoc, 
  getDoc, 
  updateDoc,
  serverTimestamp,
  collection,
  query,
  where,
  getDocs
} from 'firebase/firestore';
import { firebaseConfig, currentEnvironment } from './firebase-config';

// Initialize Firebase
const app = initializeApp(firebaseConfig);
export const auth = getAuth(app);
export const db = getFirestore(app);

// Google Auth Provider
const googleProvider = new GoogleAuthProvider();

// Disposable/temporary email domains to block
const DISPOSABLE_EMAIL_DOMAINS = [
  'tempmail.com', 'guerrillamail.com', '10minutemail.com', 'mailinator.com',
  'throwaway.email', 'temp-mail.org', 'getnada.com', 'maildrop.cc',
  'yopmail.com', 'fakeinbox.com', 'trashmail.com', 'emailondeck.com',
  'sharklasers.com', 'grr.la', 'guerrillamail.biz', 'guerrillamail.org',
  'spam4.me', 'getairmail.com', 'mintemail.com', 'mytemp.email',
  'tempail.com', 'dispostable.com', 'mohmal.com', 'trashinbox.net'
];

// Check if email is from a disposable service
export const isDisposableEmail = (email) => {
  if (!email) return false;
  const domain = email.split('@')[1]?.toLowerCase();
  return DISPOSABLE_EMAIL_DOMAINS.includes(domain);
};

// User data structure in Firestore
const createUserDocument = async (user, additionalData = {}) => {
  if (!user) return;

  const userRef = doc(db, 'users', user.uid);
  const snapshot = await getDoc(userRef);

  if (!snapshot.exists()) {
    const { email, displayName } = user;
    const createdAt = serverTimestamp();

    try {
      await setDoc(userRef, {
        email,
        displayName: displayName || email.split('@')[0],
        tier: 'free', // Default to free tier
        portfolios: [],
        createdAt,
        ...additionalData
      });
    } catch (error) {
      console.error('Error creating user document:', error);
    }
  }

  return userRef;
};

// Auth Functions
export const signUpWithEmail = async (email, password, displayName) => {
  try {
    const { user } = await createUserWithEmailAndPassword(auth, email, password);
    
    // Send email verification immediately
    await sendEmailVerification(user);
    
    // Update display name
    if (displayName) {
      await updateProfile(user, { displayName });
    }
    
    // Create user document in Firestore
    await createUserDocument(user, { displayName, emailVerified: false });
    
    return { user, error: null, verificationSent: true };
  } catch (error) {
    return { user: null, error: error.message, verificationSent: false };
  }
};

export const signInWithEmail = async (email, password) => {
  try {
    const { user } = await signInWithEmailAndPassword(auth, email, password);
    return { user, error: null };
  } catch (error) {
    return { user: null, error: error.message };
  }
};

export const signInWithGoogle = async () => {
  try {
    const { user } = await signInWithPopup(auth, googleProvider);
    await createUserDocument(user);
    return { user, error: null };
  } catch (error) {
    return { user: null, error: error.message };
  }
};

export const logOut = async () => {
  try {
    await signOut(auth);
    return { error: null };
  } catch (error) {
    return { error: error.message };
  }
};

export const resendVerificationEmail = async () => {
  try {
    const user = auth.currentUser;
    if (!user) {
      return { error: 'No user logged in' };
    }
    
    await sendEmailVerification(user);
    return { error: null };
  } catch (error) {
    return { error: error.message };
  }
};

export const resetPassword = async (email) => {
  try {
    await sendPasswordResetEmail(auth, email);
    return { error: null };
  } catch (error) {
    return { error: error.message };
  }
};

// User Data Functions
export const getUserData = async (userId) => {
  try {
    const userRef = doc(db, 'users', userId);
    const snapshot = await getDoc(userRef);
    
    if (snapshot.exists()) {
      return { data: snapshot.data(), error: null };
    } else {
      return { data: null, error: 'User not found' };
    }
  } catch (error) {
    return { data: null, error: error.message };
  }
};

export const updateUserData = async (userId, data) => {
  try {
    const userRef = doc(db, 'users', userId);
    await updateDoc(userRef, {
      ...data,
      updatedAt: serverTimestamp()
    });
    return { error: null };
  } catch (error) {
    return { error: error.message };
  }
};

// Portfolio Functions
export const savePortfolios = async (userId, portfolios) => {
  try {
    const userRef = doc(db, 'users', userId);
    await updateDoc(userRef, {
      portfolios,
      updatedAt: serverTimestamp()
    });
    return { error: null };
  } catch (error) {
    return { error: error.message };
  }
};

export const getPortfolios = async (userId) => {
  try {
    const { data, error } = await getUserData(userId);
    if (error) return { portfolios: [], error };
    return { portfolios: data?.portfolios || [], error: null };
  } catch (error) {
    return { portfolios: [], error: error.message };
  }
};

// Tier Management
export const canAddPortfolio = async (userId) => {
  try {
    const { data, error } = await getUserData(userId);
    if (error) return { canAdd: false, error };
    
    const tier = data?.tier || 'free';
    const portfolios = data?.portfolios || [];
    
    // Free tier: max 1 portfolio
    // Premium tier: unlimited
    if (tier === 'premium') {
      return { canAdd: true, reason: 'premium', error: null };
    }
    
    if (portfolios.length < 1) {
      return { canAdd: true, reason: 'free-limit-not-reached', error: null };
    }
    
    return { 
      canAdd: false, 
      reason: 'free-limit-reached',
      message: 'Free tier allows 1 portfolio. Upgrade to Premium for unlimited portfolios.',
      error: null 
    };
  } catch (error) {
    return { canAdd: false, error: error.message };
  }
};

export const upgradeToPremium = async (userId) => {
  try {
    await updateUserData(userId, { 
      tier: 'premium',
      upgradedAt: serverTimestamp()
    });
    return { error: null };
  } catch (error) {
    return { error: error.message };
  }
};

// Auth State Observer
export const onAuthChange = (callback) => {
  return onAuthStateChanged(auth, callback);
};

// Development/Testing Functions
export const isDevelopment = () => currentEnvironment === 'development';

// For testing: Force premium tier in development
export const DEV_FORCE_PREMIUM = isDevelopment();

console.log(`🔐 Auth service initialized (Environment: ${currentEnvironment})`);
